import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox,
  Grid,
  Paper
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { groupBy, map, sumBy, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { useChoices, useInput } from 'react-admin';
import { ChevronDownIcon } from '../../themes/icons/ChevronDownIcon';
import { ChevronRightIcon } from '../../themes/icons/ChevronRightIcon';

const styles = () => ({
  containerClass: {},
  gridLarge: {},
  searchLabel: {},
  paper: {},
  tableWrapper: {
    maxHeight: '400px',
    overflowY: 'scroll',
    '&::-webkit-scrollbar': {
      width: '8px',
      height: '8px'
    },
    /* Track */
    '&::-webkit-scrollbar-track': {
      background: '#ebebeb',
      borderRadius: '10px',
      margin: '2px'
    },
    /* Handle */
    '&::-webkit-scrollbar-thumb': {
      background: '#9E9E9E',
      borderRadius: '10px'
    },
    /* Handle on hover */
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#7b7a7a'
    }
  },
  table: {},
  specialCellRoot: {
    borderBottom: 'none',
    paddingLeft: '36px !important',
    paddingRight: '0px !important'
  },
  checkboxCell: {
    width: '24px'
  },
  test: {
    width: '100%'
  },
  noBorder: {
    borderBottom: 'none'
  },
  expandCell: {
    cursor: 'pointer'
  },
  checkboxRoot: {
    padding: '3px'
  },
  tableCellRoot: {
    borderBottom: 'none',
    padding: '3px !important'
  },
  EmptyLabel: {
    color: '#424242',
    fontSize: '14px',
    display: 'inline-block'
  }
});

// Group of choices
const Group = ({
  group,
  choices,
  classes,
  selection,
  handleChecked,
  optionText,
  translateChoice
}) => {
  const [expand, setExpand] = useState(false);

  const handleExpand = () => {
    setExpand(!expand);
  };

  // Choice computation functions
  const { getChoiceText, getChoiceValue } = useChoices({
    optionText,
    translateChoice
  });

  // Number of selected choices within group
  const numSelected = sumBy(choices, choice =>
    selection.includes(getChoiceValue(choice))
  );

  // Render a choice
  const renderChoice = choice => {
    const value = getChoiceValue(choice);
    return choice ? (
      <TableRow key={value}>
        <TableCell
          padding="checkbox"
          className={classes.checkboxCell}
          classes={{ root: classes.specialCellRoot }}
        >
          <Checkbox
            id={getChoiceText(choice)}
            checked={selection.includes(value)}
            onChange={(event, isChecked) => handleChecked([value], isChecked)}
            classes={{ root: classes.checkboxRoot }}
          />
        </TableCell>
        <TableCell classes={{ root: classes.tableCellRoot }}>
          {getChoiceText(choice)}
        </TableCell>
      </TableRow>
    ) : null;
  };

  return (
    <>
      <TableRow>
        <TableCell classes={{ root: classes.noBorder }} colSpan={5}>
          <Grid container alignItems="center">
            <Grid
              item
              onClick={handleExpand}
              classes={{ root: classes.expandCell }}
            >
              {expand ? <ChevronDownIcon /> : <ChevronRightIcon />}
            </Grid>
            <Grid item>
              <Checkbox
                checked={Number(numSelected) === choices.length}
                indeterminate={
                  Number(numSelected) !== choices.length && numSelected > 0
                }
                classes={{ root: classes.checkboxRoot }}
                onChange={(event, isChecked) =>
                  handleChecked(
                    choices.map(choice => getChoiceValue(choice)),
                    isChecked
                  )
                }
                id={group}
              />
              {group}
            </Grid>
          </Grid>
        </TableCell>
      </TableRow>
      {expand && choices.map(choice => renderChoice(choice))}
    </>
  );
};

/**
 * Displays a list of choices in agrid with grouping
 * @param {*} props
 */
const TreeInput = props => {
  const {
    choices = [],
    classes,
    className,
    hasFilters,
    filterFunction,
    format,
    groupProp,
    helperText,
    label,
    onBlur,
    onChange,
    onFocus,
    options,
    optionText,
    optionTextLabel,
    parse,
    resource,
    source,
    translateChoice,
    validate,
    ...rest
  } = props;

  const {
    input: { onChange: finalFormOnChange, onBlur: finalFormOnBlur, value }
  } = useInput({
    format,
    onBlur,
    onChange,
    onFocus,
    parse,
    resource,
    source,
    validate,
    ...rest
  });

  // Tree choices (Memoized)
  const treeChoices = useMemo(() => {
    return filterFunction ? filterFunction(choices) : choices;
  }, [choices, filterFunction]);

  // Choice computation functions
  const { getChoiceValue } = useChoices({
    optionText,
    translateChoice
  });

  // Selected items
  const selection = useMemo(() => value || [], [value]);

  // Choice checked / unchecked
  const handleChecked = useCallback(
    (newValue, isChecked) => {
      if (isChecked) {
        finalFormOnChange([...(value || []), ...newValue]);
      } else {
        finalFormOnChange(value.filter(v => !newValue.includes(v)));
      }
      finalFormOnBlur(); // HACK: See https://github.com/final-form/react-final-form/issues/365#issuecomment-515045503
    },
    [finalFormOnChange, finalFormOnBlur, value]
  );

  // Check / uncheck all
  const handleAllChecked = useCallback(
    (event, isChecked) => {
      if (isChecked) {
        finalFormOnChange(treeChoices.map(choice => getChoiceValue(choice)));
      } else {
        finalFormOnChange([]);
      }
      finalFormOnBlur(); // HACK: See https://github.com/final-form/react-final-form/issues/365#issuecomment-515045503
    },
    [finalFormOnChange, finalFormOnBlur, treeChoices, getChoiceValue]
  );

  // Filter functions
  const [filter, setFilter] = useState('ALL');

  // Filtered choices (Memoized)
  const filteredChoices = useMemo(() => {
    if (filter === 'ALL') {
      return treeChoices;
    }
    return treeChoices.filter(choice => {
      const text = getChoiceValue(choice);
      const selectedText = selection.find(item => item === text);
      return (
        text !== undefined &&
        text.toLowerCase().includes(selectedText && selectedText.toLowerCase())
      );
    });
  }, [treeChoices, filter, getChoiceValue, selection]);

  // Grouped choices (memoized)
  const groupedChoices = useMemo(() => {
    return groupBy(filteredChoices, groupProp);
  }, [filteredChoices, groupProp]);

  return (
    <Grid item xs={12} className={classes.gridLarge}>
      {hasFilters && (
        <Grid container justifyContent="flex-end">
          <RadioGroup
            aria-label="groupsFilter"
            name="groupsFilter"
            value={filter}
            onChange={({ target }) => setFilter(target.value)}
            row
          >
            <FormControlLabel
              value="ALL"
              control={<Radio color="primary" />}
              label="Show All"
            />
            <FormControlLabel
              value="ONLY_ASSIGNED"
              control={<Radio color="primary" />}
              label="Show Assigned"
            />
          </RadioGroup>
        </Grid>
      )}

      <div className={classes.tableWrapper}>
        <Paper className={classes.paper}>
          {!isEmpty(groupedChoices) ? (
            <Table className={classes.table}>
              <TableBody>
                <TableRow>
                  <TableCell classes={{ root: classes.noBorder }} colSpan={5}>
                    <Grid container alignItems="center">
                      <Grid item>
                        <ChevronDownIcon style={{ color: 'transparent' }} />
                      </Grid>
                      <Checkbox
                        id="SelectAll"
                        indeterminate={
                          selection.length > 0 &&
                          selection.length < filteredChoices.length
                        }
                        checked={
                          selection.length > 0 &&
                          selection.length === filteredChoices.length
                        }
                        classes={{ root: classes.checkboxRoot }}
                        onChange={handleAllChecked}
                      />
                      Select All
                    </Grid>
                  </TableCell>
                </TableRow>
                {map(groupedChoices, (items, group) => (
                  <Group
                    {...props}
                    key={group}
                    choices={items}
                    group={group}
                    classes={classes}
                    selection={selection}
                    handleChecked={handleChecked}
                  />
                ))}
              </TableBody>
            </Table>
          ) : (
            <span className={classes.emptyLabel}>
              There are no Access Roles to be displayed
            </span>
          )}
        </Paper>
      </div>
    </Grid>
  );
};

TreeInput.propTypes = {
  choices: PropTypes.arrayOf(PropTypes.object),
  classes: PropTypes.object,
  className: PropTypes.string,
  children: PropTypes.node,
  groupProp: PropTypes.string,
  hasFilters: PropTypes.bool,
  filterFunction: PropTypes.func,
  label: PropTypes.string,
  options: PropTypes.object,
  optionText: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.element
  ]).isRequired,
  optionTextLabel: PropTypes.string,
  resource: PropTypes.string,
  source: PropTypes.string,
  translateChoice: PropTypes.bool
};

TreeInput.defaultProps = {
  hasFilters: true,
  options: {},
  optionText: 'name',
  translateChoice: true,
  filterFunction: undefined
};

export default withStyles(styles)(TreeInput);
